import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Image as ChakraImage,
  Image,
  Spacer,
  Stack,
  Tag,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Order, OrderWithDeliveryOrders } from '@diamond/shared/types';
import {
  buyerCurrencyFormatter,
  LIST_FEATURE_FLAGS,
  numberWithSeparator,
  statusFormatter,
  useFeatureFlag,
} from '@diamond/shared/utils';
import { SO_STATUS_CONFIG, SOStatus } from '@diamond/sol-buyer/constant';
import {
  BindedPromoData,
  BundledDiscountPromo,
  getOrderDetailProductDisplay,
  ProductWithPromo,
  SingleDiscountPromo,
  useOrderActions,
} from '@diamond/sol-buyer/utils';
import { OrderDetailPromoPrice } from '@diamond/sol-buyer-components';
import { useShipToStore } from '@diamond/sol-buyer-context';
import { useRouter } from 'next/router';
import shallow from 'zustand/shallow';

import { StatusBadge } from '../product-table/status-badge';

interface ArrayProductData {
  id: string;
  note: string | undefined;
  quantity: number;
}

interface MobileProductTableProps {
  data: BindedPromoData;
  isSubmitted?: boolean;
  extra?: React.ReactNode;
  order?: Order;
  accessToken?: string;
  deliveryOrders?: OrderWithDeliveryOrders[];
  poDetailsDataEnabled?: boolean;
}

const emptyArr: ArrayProductData[] = [];

const safeNoteValue = (note: unknown): string | undefined => {
  if (note === null || note === undefined || note === '') {
    return undefined;
  }
  const noteStr = String(note).trim();
  if (noteStr === '' || noteStr === 'null' || noteStr === 'undefined') {
    return undefined;
  }
  return noteStr;
};

export function MobileProductTable({
  data,
  isSubmitted,
  extra,
  order,
  accessToken,
  deliveryOrders: _deliveryOrders,
  poDetailsDataEnabled = false,
}: MobileProductTableProps) {
  const router = useRouter();
  const isVoucherMenuEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_VOUCHERS_MENU,
    true
  );

  const [selectedShipToId] = useShipToStore(
    (s) => [s.selectedShipToId],
    shallow
  );
  const isOrderDetailRoute =
    window.location.pathname.startsWith('/order-detail');

  const arrProductData: ArrayProductData[] =
    data && isOrderDetailRoute
      ? data.flatMap((value) =>
          value.products.map((v) => ({
            id: v.id,
            note: safeNoteValue(v.note),
            quantity: Number(v.quantity),
          }))
        )
      : emptyArr;

  const { handleBuyAgain, isSubmitting: isBuyAgainLoading } = useOrderActions({
    router,
    accessToken: accessToken || '',
    selectedShipToId,
    warehouseId: selectedShipToId,
    products: arrProductData,
    order: order || undefined,
  });

  return (
    <Box>
      <Box pb="4" fontSize="14px">
        <Text fontSize="sm" fontWeight="bold">
          Rincian Produk
        </Text>
        {data?.map((item, index) => {
          const bundledCashbacks = item.promos.bundled_discount_promos.filter(
            (promo: BundledDiscountPromo) => !promo.is_discount
          );
          const singleCashbacks = item.promos.single_discount_promos.filter(
            (promo: SingleDiscountPromo) => !promo.is_discount
          );
          const cashbacks = [...singleCashbacks, ...bundledCashbacks];

          const displayStatus = item.status;

          return (
            <Box key={index}>
              <Flex
                color="white"
                bg="#0941A8"
                roundedTop="md"
                p="2"
                mt={index === 0 ? 4 : 6}
                alignItems="center"
                gap="2"
              >
                <Flex gap="1" alignItems="center">
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    textTransform="uppercase"
                  >
                    {item.divider}
                  </Text>
                  {isSubmitted && (
                    <>
                      <Text fontSize="xs" fontWeight="bold">
                        •
                      </Text>
                      <Text fontSize="xs" textTransform="uppercase">
                        {item.sub_po_number}
                      </Text>
                    </>
                  )}
                </Flex>
                <StatusBadge
                  status={displayStatus}
                  reason={item.reason}
                  sapNote={item.sap_note}
                />
              </Flex>
              {item.products?.map(
                (product: ProductWithPromo, productIdx: number) => {
                  const display = getOrderDetailProductDisplay(
                    product,
                    poDetailsDataEnabled
                  );

                  const productDesc =
                    product.desc.length > 40
                      ? `${product.desc.slice(0, 40)} ...`
                      : product.desc;

                  const productNote = safeNoteValue(product.note);

                  return (
                    <Stack
                      opacity={product.is_not_processed ? 0.7 : 1}
                      key={`product-${index}-${productIdx}`}
                      border="1px"
                      borderColor="gray.33"
                      roundedBottom={
                        productIdx === item.products.length - 1
                          ? 'md'
                          : undefined
                      }
                      p="4"
                      spacing="4"
                    >
                      <Flex w="full" alignItems="start">
                        <Image
                          as={ChakraImage}
                          src={
                            product.product_images_medium?.[0] ||
                            '/images/product_placeholder.png'
                          }
                          alt={product.title}
                          objectFit="scale-down"
                          borderRadius="md"
                          border="1px"
                          borderColor="gray.33"
                          w="40px"
                          h="40px"
                          fallbackSrc="/images/product_placeholder.png"
                        />
                        <Box
                          justifyContent="center"
                          alignItems="center"
                          mx="2"
                          minW={0}
                          maxW={{ base: '200px', md: '100%' }}
                        >
                          <Flex
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              textAlign="start"
                              noOfLines={1}
                            >
                              {product.brand_desc}
                            </Text>
                            {product.is_not_processed ? (
                              <Tag
                                ml={2}
                                borderRadius="full"
                                background="gray"
                                color="white"
                                fontWeight="bold"
                                flexShrink={0}
                              >
                                Dibatalkan
                              </Tag>
                            ) : null}
                          </Flex>
                          <Text fontSize="xs" noOfLines={1}>
                            {productDesc}
                          </Text>
                          <Text fontSize="2xs" textColor="gray.75">
                            {product.item_code}
                          </Text>
                          {productNote && (
                            <Text
                              fontSize="2xs"
                              textColor="blue.500"
                              mt="1"
                              fontStyle="italic"
                            >
                              Catatan: {productNote}
                            </Text>
                          )}
                          <Badge
                            fontSize="12px"
                            rounded="lg"
                            my="2"
                            px="2"
                            py="1"
                            bg="green"
                            color="white"
                            display={
                              product.is_new_product ? 'inline-block' : 'none'
                            }
                          >
                            Ditambahkan Oleh Admin
                          </Badge>
                        </Box>
                        <Spacer />
                        <Box>
                          <Text fontSize="sm" fontWeight="bold">
                            x{numberWithSeparator(display.confirmedQuantity)}
                          </Text>
                        </Box>
                      </Flex>
                      {display.showPriceSection ? (
                        <HStack justifyContent="space-between">
                          <Box>
                            <Text color="gray" fontSize="xs">
                              Harga Satuan
                            </Text>
                            {isVoucherMenuEnabled && (
                              <Box mb="1">
                                <OrderDetailPromoPrice
                                  product={product}
                                  isVoucherMenuEnabled={isVoucherMenuEnabled}
                                  originalUnitPrice={display.originalUnitPrice}
                                />
                              </Box>
                            )}
                            <Text fontWeight="bold" fontSize="sm">
                              {buyerCurrencyFormatter(display.unitPrice)}
                            </Text>
                          </Box>
                          <Box>
                            <Text color="gray" fontSize="xs">
                              Total Harga
                            </Text>
                            <Text fontWeight="bold" fontSize="sm">
                              {buyerCurrencyFormatter(display.totalPrice)}
                            </Text>
                          </Box>
                        </HStack>
                      ) : null}
                      {(display.showInitialQuantity ||
                        display.showInitialUnitPrice ||
                        display.showInitialTotalPrice) && (
                        <Box color="gray.75">
                          {display.showInitialQuantity ? (
                            <Box>
                              <Text>
                                Pesanan Awal{' '}
                                <span style={{ fontWeight: 'bold' }}>
                                  x{display.quantity}
                                </span>
                              </Text>
                            </Box>
                          ) : null}

                          {(display.showInitialUnitPrice ||
                            display.showInitialTotalPrice) && (
                            <HStack justifyContent="space-between">
                              {display.showInitialUnitPrice ? (
                                <Box>
                                  <Text pt={2}>Harga Awal</Text>
                                  <Text>
                                    {buyerCurrencyFormatter(
                                      display.initialUnitPrice
                                    )}
                                  </Text>
                                </Box>
                              ) : null}
                              {display.showInitialTotalPrice ? (
                                <Box>
                                  <Text pt={2}>Total Awal</Text>
                                  <Text>
                                    {buyerCurrencyFormatter(
                                      display.initialTotalPrice
                                    )}
                                  </Text>
                                </Box>
                              ) : null}
                            </HStack>
                          )}
                        </Box>
                      )}
                    </Stack>
                  );
                }
              )}
            </Box>
          );
        })}
      </Box>

      <Text
        fontWeight="400"
        fontStyle="normal"
        fontSize="12px"
        lineHeight="100%"
        letterSpacing="0%"
        color="#8E8E93"
        mb="4"
      >
        Harga satuan pada PO dan faktur dapat berbeda karena perhitungan
        pembulatan.
      </Text>

      {extra && (
        <Box
          rounded="lg"
          border="1px"
          borderColor="gray.33"
          shadow="base"
          my="1"
          mb="4"
          p="4"
        >
          {extra}
        </Box>
      )}
      <VStack display={isOrderDetailRoute ? 'flex' : 'none'}>
        <Button
          variant="solid"
          width="full"
          fontSize="14px"
          onClick={() => void handleBuyAgain()}
          isLoading={isBuyAgainLoading}
        >
          <span>Beli Lagi</span>
        </Button>
        <Button
          variant="outline"
          width="full"
          fontSize="14px"
          onClick={() =>
            router.push({
              pathname: '/daftar-belanja/create',
              query: { ...router.query, type: 'order_detail' },
            })
          }
        >
          Buat Daftar Belanja
        </Button>
      </VStack>
    </Box>
  );
}
